/**
 * Concurrent Pool - Sliding window concurrency control for parallel uploads
 *
 * Uses a sliding window approach instead of fixed batches:
 * - Keeps N tasks running at all times
 * - When one completes, immediately starts the next
 * - More efficient than Promise.all batches (no waiting for slowest in batch)
 */

export interface ConcurrentPoolOptions {
  concurrency: number
  shouldAbort?: () => boolean
  onTaskStart?: (index: number) => void
  onTaskComplete?: (index: number) => void
  onTaskError?: (index: number, error: Error) => void
}

export interface TaskResult<T> {
  index: number
  success: boolean
  result?: T
  error?: Error
}

export interface Task<T> {
  index: number
  task: () => Promise<T>
}

export class ConcurrentPool<T> {
  private concurrency: number
  private shouldAbort: () => boolean
  private onTaskStart?: (index: number) => void
  private onTaskComplete?: (index: number) => void
  private onTaskError?: (index: number, error: Error) => void
  private aborted: boolean = false

  constructor(options: ConcurrentPoolOptions) {
    this.concurrency = options.concurrency
    this.shouldAbort = options.shouldAbort || (() => false)
    this.onTaskStart = options.onTaskStart
    this.onTaskComplete = options.onTaskComplete
    this.onTaskError = options.onTaskError
  }

  /**
   * Execute all tasks with controlled concurrency
   * Returns results for all tasks (success or failure)
   */
  async execute(tasks: Task<T>[]): Promise<TaskResult<T>[]> {
    if (tasks.length === 0) {
      return []
    }

    const results: TaskResult<T>[] = []
    let nextIndex = 0
    let runningCount = 0

    return new Promise((resolve) => {
      const startNext = () => {
        // Check abort condition before starting new tasks
        if (this.aborted || this.shouldAbort()) {
          if (runningCount === 0) {
            resolve(results)
          }
          return
        }

        // Start tasks up to concurrency limit
        while (runningCount < this.concurrency && nextIndex < tasks.length) {
          const taskItem = tasks[nextIndex]
          nextIndex++
          runningCount++

          this.onTaskStart?.(taskItem.index)

          taskItem
            .task()
            .then((result) => {
              results.push({
                index: taskItem.index,
                success: true,
                result,
              })
              this.onTaskComplete?.(taskItem.index)
            })
            .catch((error) => {
              results.push({
                index: taskItem.index,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
              })
              this.onTaskError?.(taskItem.index, error)
            })
            .finally(() => {
              runningCount--

              // All done?
              if (nextIndex >= tasks.length && runningCount === 0) {
                resolve(results)
                return
              }

              // Start next task
              startNext()
            })
        }
      }

      // Kick off initial batch
      startNext()
    })
  }

  /**
   * Abort all pending tasks
   * Already running tasks will complete, but no new tasks will start
   */
  abort(): void {
    this.aborted = true
  }
}

/**
 * Utility function to create and execute a pool in one call
 */
export async function runConcurrent<T>(
  tasks: Task<T>[],
  options: ConcurrentPoolOptions,
): Promise<TaskResult<T>[]> {
  const pool = new ConcurrentPool<T>(options)
  return pool.execute(tasks)
}
